import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight, Utensils, Star, Heart, Flame, Leaf, MapPin, Phone, ChefHat, Award, Sparkles, Coffee, Salad } from "lucide-react";
import { useTranslation } from "react-i18next";
import Hero3D from "@/components/Hero3D";
import MarqueeStrip from "@/components/MarqueeStrip";
import { api } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";

const PROMISE_ICONS = {
  leaf: Leaf, heart: Heart, flame: Flame, chef: ChefHat, star: Star,
  utensils: Utensils, award: Award, sparkles: Sparkles, coffee: Coffee, salad: Salad,
};

export default function Home() {
  const { settings } = useSettings();
  const { addItem } = useCart();
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith("zh");
  const [items, setItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [google, setGoogle] = useState({ enabled: false, reviews: [] });

  useEffect(() => {
    api.get("/menu", { params: { available_only: true } }).then((r) => setItems(r.data));
    api.get("/reviews", { params: { approved_only: true } }).then((r) => setReviews(r.data));
    api.get("/gallery").then((r) => setGallery(r.data));
    api.get("/google-reviews").then((r) => setGoogle(r.data)).catch(() => {});
  }, []);

  const specials = items.filter((i) => i.is_special).slice(0, 6);
  const dispName = (i) => (isZh && i.name_zh) ? i.name_zh : i.name;

  const handleAdd = (item) => {
    addItem(item);
    toast.success(dispName(item), { description: `NT$ ${item.price}` });
  };

  return (
    <div data-testid="home-page">
      {/* ============= HERO ============= */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-24">
        <div className="absolute inset-0 mandala-bg" />

        {/* Decorative photo on right (hidden if hero_image_url set) */}
        <div className="absolute inset-y-0 right-0 w-1/2 hidden lg:block" aria-hidden="true">
          <div
            className="w-full h-full opacity-[0.13]"
            style={{
              backgroundImage: "url(https://images.unsplash.com/photo-1772730064951-89b427965dbc?w=1200)",
              backgroundSize: "cover", backgroundPosition: "center",
              maskImage: "linear-gradient(90deg, transparent, black 50%)",
              WebkitMaskImage: "linear-gradient(90deg, transparent, black 50%)",
            }}
          />
        </div>

        <div className="container-tulsi relative z-10 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center py-12">
          {/* LEFT — Editorial */}
          <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            {/* Eyebrow */}
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-16" style={{ background: "linear-gradient(90deg, transparent, #C9A227)" }} />
              <span className="text-[10px] tracking-[0.5em] uppercase text-[#A90011] font-medium">{t("home.welcomeTo")} · Est. 2023</span>
              <span className="h-px w-16" style={{ background: "linear-gradient(90deg, #C9A227, transparent)" }} />
            </div>

            {/* Display title */}
            <h1 className="font-serif-display text-[clamp(4.5rem,13vw,11rem)] leading-[0.82] text-gold-gradient mb-2 -ml-1">
              TULSI
            </h1>
            <p className="font-script text-5xl md:text-6xl text-[#A90011] mb-3 leading-none">杜爾希</p>
            <p className="text-sm md:text-base tracking-[0.35em] uppercase text-[#6B4A4E] mb-8 font-medium">{t("home.subtitle")}</p>

            <div className="flex gap-3 items-start mb-10">
              <span className="text-[#C9A227] text-3xl font-serif leading-none mt-1">"</span>
              <p className="font-serif italic text-xl md:text-2xl text-[#2A0708] max-w-lg leading-snug">
                {settings?.tagline || "Hand-ground masalas. Tandoor fire. Soul of India in Taichung."}
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/menu"><button className="btn-saffron" data-testid="hero-menu-btn"><Utensils size={14} /> {t("home.viewMenu")}</button></Link>
              <Link to="/order"><button className="btn-outline-gold" data-testid="hero-order-btn">{t("home.orderAtTable")} <ArrowRight size={14} /></button></Link>
              <Link to="/reservations">
                <button className="text-xs px-7 py-3 font-medium tracking-[0.18em] uppercase border-b transition-colors text-[#6B4A4E] hover:text-[#A90011]" style={{ borderColor: "#C9A227" }} data-testid="hero-book-btn">
                  {t("home.reserveTable")}
                </button>
              </Link>
            </div>

            {/* Quick info chips */}
            <div className="flex flex-wrap gap-6 mt-12 text-xs tracking-wider uppercase text-[#6B4A4E]">
              <span className="flex items-center gap-2"><MapPin size={12} className="text-[#C9A227]" /> {settings?.address?.split(",")[0] || "Taichung"}</span>
              <span className="flex items-center gap-2"><Phone size={12} className="text-[#C9A227]" /> {settings?.phone}</span>
              <span className="flex items-center gap-2 text-[#A90011]"><span className="w-1.5 h-1.5 rounded-full bg-[#A90011] animate-pulse" /> {t("contact.closedTuesdays")}</span>
            </div>
          </motion.div>

          {/* RIGHT — Stage with 3D + wax badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-[500px] md:h-[620px]"
          >
            <div className="ornament-corner tl" />
            <div className="ornament-corner tr" />
            <div className="ornament-corner bl" />
            <div className="ornament-corner br" />

            {/* Wax seal badge — floats top-right */}
            <div className="absolute -top-2 -right-2 z-20 wax-badge" data-testid="wax-badge">
              <span className="text-[9px] tracking-[0.25em] uppercase mt-[26px] opacity-90">Chef's</span>
              <span className="font-serif-display text-[26px] leading-none">Best</span>
              <span className="text-[9px] tracking-[0.25em] uppercase mt-1 opacity-90">2026</span>
            </div>

            <div className="w-full h-full rounded-3xl overflow-hidden" style={{ background: "radial-gradient(ellipse at center, #FBEBD8 0%, #F4DDC0 70%, transparent 100%)" }}>
              {settings?.hero_image_url ? (
                <img src={settings.hero_image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Hero3D />
              )}
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-float text-center">
          <p className="text-[9px] tracking-[0.4em] uppercase text-[#8E5F65] mb-1">Scroll</p>
          <ChevronDown size={20} className="text-[#C9A227] mx-auto" />
        </div>
      </section>

      {/* === MARQUEE STRIP === */}
      <MarqueeStrip />

      {/* ============= PROMISE ============= */}
      <section className="section-py relative" data-testid="promise-section">
        <div className="container-tulsi">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-[10px] tracking-[0.5em] uppercase text-[#A90011] mb-4 font-medium">— {settings?.promise_eyebrow || t("home.ourPromise")} —</p>
            <h2 className="font-serif-display text-5xl md:text-7xl text-gold-gradient mb-2 leading-[1.05]">{settings?.promise_title || t("home.promiseTitle")}</h2>
            <div className="gold-separator" />
            <p className="font-serif italic text-lg md:text-xl text-[#2A0708] leading-relaxed mt-8 max-w-2xl mx-auto">
              {settings?.promise_text}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20">
            {[
              { iconKey: settings?.promise_card1_icon || "leaf", title: settings?.promise_card1_title || t("home.promiseCard1Title"), desc: settings?.promise_card1_desc || t("home.promiseCard1Desc"), num: "01" },
              { iconKey: settings?.promise_card2_icon || "heart", title: settings?.promise_card2_title || t("home.promiseCard2Title"), desc: settings?.promise_card2_desc || t("home.promiseCard2Desc"), num: "02" },
              { iconKey: settings?.promise_card3_icon || "flame", title: settings?.promise_card3_title || t("home.promiseCard3Title"), desc: settings?.promise_card3_desc || t("home.promiseCard3Desc"), num: "03" },
            ].map(({ iconKey, title, desc, num }, i) => {
              const Icon = PROMISE_ICONS[iconKey] || Leaf;
              return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.7 }}
                whileHover={{ y: -6 }}
                className="glass-card p-10 text-center relative overflow-hidden group"
                data-testid={`promise-card-${i}`}
              >
                <span className="absolute top-3 right-5 font-serif-display text-7xl text-[#FBEBD8] leading-none select-none">{num}</span>
                <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full mb-5" style={{ background: "linear-gradient(135deg, rgba(209,29,33,0.1), rgba(201,162,39,0.15))", border: "1px solid rgba(201,162,39,0.4)" }}>
                  <Icon size={26} className="text-[#A90011]" />
                </div>
                <h3 className="font-serif-display text-3xl text-[#2A0708] mb-3">{title}</h3>
                <p className="text-sm text-[#6B4A4E] leading-relaxed">{desc}</p>
                <div className="mt-5 h-px w-12 mx-auto" style={{ background: "linear-gradient(90deg, transparent, #C9A227, transparent)" }} />
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============= CHEF'S SPECIALS ============= */}
      {specials.length > 0 && (
        <section
          className="section-py relative"
          style={{ background: "linear-gradient(180deg, transparent, rgba(244, 221, 192, 0.5), transparent)" }}
          data-testid="specials-section"
        >
          <div className="container-tulsi">
            <div className="text-center mb-16">
              <p className="text-[10px] tracking-[0.5em] uppercase text-[#A90011] mb-4 font-medium">— {t("home.chefSelection")} —</p>
              <h2 className="font-serif-display text-5xl md:text-7xl text-gold-gradient leading-[1.05]">{t("home.signatureDishes")}</h2>
              <div className="gold-separator" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {specials.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.7 }}
                  whileHover={{ y: -8 }}
                  className="glass-card overflow-hidden group cursor-pointer"
                  data-testid={`special-card-${item.id}`}
                >
                  {item.image_url ? (
                    <div className="aspect-[5/4] overflow-hidden frame-elegant rounded-none border-b border-[#E8D9BE]">
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
                    </div>
                  ) : (
                    <div className="aspect-[5/4] flex items-center justify-center relative" style={{ background: "linear-gradient(135deg, #FBEBD8, #F4DDC0)" }}>
                      <Utensils size={48} className="text-[#C9A227]/40" />
                      <span className="absolute bottom-3 right-4 text-[10px] tracking-[0.3em] uppercase text-[#8E5F65]">Photo coming soon</span>
                    </div>
                  )}
                  <div className="p-7">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-serif-display text-2xl text-[#2A0708] leading-tight">{dispName(item)}</h3>
                        {isZh && item.name && <p className="text-xs uppercase tracking-wider text-[#8E5F65] mt-1">{item.name}</p>}
                        {!isZh && item.name_zh && <p className="text-xs text-[#8E5F65] mt-1">{item.name_zh}</p>}
                      </div>
                      <span className="font-serif-display text-2xl text-[#A90011] whitespace-nowrap">NT$ {item.price}</span>
                    </div>
                    <p className="text-sm text-[#6B4A4E] mb-5 line-clamp-2 leading-relaxed">{isZh && item.description_zh ? item.description_zh : item.description}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-dashed border-[#E8D9BE]">
                      <div className="flex gap-1.5 items-center">
                        {item.is_veg && <span className="text-[9px] tracking-widest uppercase text-green-700 border border-green-700/30 px-2 py-0.5 rounded">{t("menu.veg")}</span>}
                        {item.is_spicy && <Flame size={13} className="text-[#D11D21]" />}
                        {item.is_popular && <Star size={13} className="text-[#C9A227]" fill="currentColor" />}
                      </div>
                      <button onClick={() => handleAdd(item)} className="text-[10px] tracking-[0.2em] uppercase text-[#A90011] hover:text-[#D11D21] flex items-center gap-1 font-medium" data-testid={`add-to-cart-${item.id}`}>
                        + {t("menu.add")} →
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-16">
              <Link to="/menu"><button className="btn-outline-gold" data-testid="view-full-menu-btn">{t("home.exploreMenu")} <ArrowRight size={14} /></button></Link>
            </div>
          </div>
        </section>
      )}

      {/* ============= GALLERY ============= */}
      {gallery.length > 0 && (
        <section className="section-py" data-testid="gallery-preview">
          <div className="container-tulsi">
            <div className="text-center mb-14">
              <p className="text-[10px] tracking-[0.5em] uppercase text-[#A90011] mb-4 font-medium">— {t("home.moments")} —</p>
              <h2 className="font-serif-display text-5xl md:text-7xl text-gold-gradient leading-[1.05]">{t("home.gallery")}</h2>
              <div className="gold-separator" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.slice(0, 8).map((g, i) => (
                <motion.div
                  key={g.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  className="aspect-square overflow-hidden rounded-xl border border-[#E8D9BE] group frame-elegant"
                  data-testid={`gallery-thumb-${g.id}`}
                >
                  <img src={g.image_url} alt={g.caption} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1.2s]" />
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/gallery"><button className="btn-outline-gold" data-testid="view-gallery-btn">{t("home.viewFullGallery")} <ArrowRight size={14} /></button></Link>
            </div>
          </div>
        </section>
      )}

      {/* ============= REVIEWS ============= */}
      <section className="section-py relative" data-testid="reviews-section">
        <div className="absolute inset-0 mandala-bg" />
        <div className="container-tulsi relative z-10">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.5em] uppercase text-[#A90011] mb-4 font-medium">— {t("home.kindWords")} —</p>
            <h2 className="font-serif-display text-5xl md:text-7xl text-gold-gradient leading-[1.05]">{t("home.guestReviews")}</h2>
            <div className="gold-separator" />
            {google.enabled && google.place && (
              <p className="mt-6 text-sm text-[#6B4A4E] inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C9A227]/40 bg-[#FFFAEF]">
                <Star size={14} className="text-[#C9A227]" fill="currentColor" />
                <span className="font-medium text-[#2A0708]">{google.place.rating}</span> from <span className="text-[#2A0708]">{google.place.total}</span> Google reviews
              </p>
            )}
          </div>

          {google.enabled && google.reviews.length > 0 && (
            <div className="mb-12">
              <p className="text-center text-[10px] tracking-[0.4em] uppercase text-[#8B6F1A] mb-6">— {t("home.fromGoogle")} —</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {google.reviews.slice(0, 3).map((r, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="glass-card p-7" data-testid={`google-review-${i}`}>
                    <div className="flex items-center gap-3 mb-4">
                      {r.photo && <img src={r.photo} alt="" className="w-10 h-10 rounded-full border border-[#E8D9BE]" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#2A0708] truncate font-medium">{r.name}</p>
                        <p className="text-[10px] uppercase tracking-widest text-[#8E5F65]">{r.time}</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-3">{[...Array(r.rating)].map((_, k) => <Star key={k} size={12} className="text-[#C9A227]" fill="currentColor" />)}</div>
                    <p className="text-sm text-[#6B4A4E] line-clamp-5 italic leading-relaxed">"{r.text}"</p>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {reviews.length > 0 && (
            <>
              {google.enabled && <p className="text-center text-[10px] tracking-[0.4em] uppercase text-[#8B6F1A] mb-6">— {t("home.directOnSite")} —</p>}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {reviews.slice(0, 3).map((r, i) => (
                  <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="glass-card p-7" data-testid={`review-card-${r.id}`}>
                    <span className="font-serif-display text-6xl text-[#C9A227]/30 leading-none block -mt-2">"</span>
                    <p className="text-[#2A0708] italic mb-4 -mt-3 leading-relaxed">{r.comment}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-dashed border-[#E8D9BE]">
                      <p className="text-xs tracking-[0.2em] uppercase text-[#A90011] font-medium">— {r.name}</p>
                      <div className="flex gap-0.5">{[...Array(r.rating)].map((_, k) => <Star key={k} size={11} className="text-[#C9A227]" fill="currentColor" />)}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}

          <div className="text-center mt-12 flex flex-wrap items-center justify-center gap-4">
            {(settings?.google_review_url || google.place?.url) && (
              <a href={settings?.google_review_url || google.place.url} target="_blank" rel="noreferrer">
                <button className="btn-outline-gold" data-testid="google-reviews-btn">{t("home.seeGoogleReviews")} <ArrowRight size={14} /></button>
              </a>
            )}
            <Link to="/reviews"><button className="text-xs tracking-[0.25em] uppercase text-[#6B4A4E] hover:text-[#A90011]" data-testid="write-review-link">{t("home.shareExperience")}</button></Link>
          </div>
        </div>
      </section>

      {/* === FINAL MARQUEE === */}
      <MarqueeStrip />
    </div>
  );
}
